import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogContent, DialogTitle,
  Divider, Paper, Stack, TextField, Typography, useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { getDevtoolsAuthToken } from "../../utils/devtoolsAuth";
import ManagedDocumentImage from "../../../docs/document_manager/ManagedDocumentImage";

// Read-only view of the SAME project-scoped documents served by Document Manager.
// In particular, never copy documents into a Full Change-specific store.
const docsHeaders = () => {
  const token = getDevtoolsAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};
const readableTime = (date) => {
  if (!date) return "Not recorded";
  const parsed = new Date(date);
  return Number.isNaN(parsed.getTime()) ? String(date) : parsed.toLocaleString();
};
const isMatchingPlanningDoc = (doc, projectId, fullChangeId) => Boolean(
  doc && doc.slug && doc.category === "Planning" &&
  String(doc.full_change_id) === String(fullChangeId) &&
  (!doc.project_id || String(doc.project_id) === String(projectId))
);

// Do not inject raw HTML. Paths in the Markdown are untrusted, including paths
// that look like relative image references. Full Change does not yet expose an
// authenticated planning-asset endpoint; preserve such references as text.
const inlineText = (source, prefix = "inline") => {
  const output = [];
  const text = String(source || "");
  const pattern = /(!?\[([^\]]+)\]\(([^)]+)\)|`([^`]+)`|\*\*([^*]+)\*\*|\*([^*]+)\*)/g;
  let cursor = 0;
  let match;
  while ((match = pattern.exec(text)) !== null) {
    if (match.index > cursor) output.push(text.slice(cursor, match.index));
    const key = `${prefix}-${match.index}`;
    if (match[2] !== undefined) {
      const target = String(match[3] || "").trim();
      const safeHttp = /^https?:\/\/[^\s<>"']+$/i.test(target);
      if (match[1].startsWith("!")) {
        output.push(<Box key={key} component="span" sx={{ display: "inline", color: "text.secondary" }}>[Image: {match[2]} — {target || "no path"}; asset reference preserved; original Markdown is in Document Manager]</Box>);
      } else if (safeHttp) {
        output.push(<Box component="a" key={key} href={target} target="_blank" rel="noopener noreferrer" sx={{ color: "primary.main", overflowWrap: "anywhere" }}>{match[2]}</Box>);
      } else {
        output.push(<Box key={key} component="span">{match[2]} ({target})</Box>);
      }
    } else if (match[4] !== undefined) {
      output.push(<Box key={key} component="code" sx={{ bgcolor: "action.hover", px: 0.3, borderRadius: 0.5, fontFamily: "monospace", overflowWrap: "anywhere" }}>{match[4]}</Box>);
    } else if (match[5] !== undefined) {
      output.push(<strong key={key}>{match[5]}</strong>);
    } else if (match[6] !== undefined) {
      output.push(<em key={key}>{match[6]}</em>);
    }
    cursor = pattern.lastIndex;
  }
  if (cursor < text.length) output.push(text.slice(cursor));
  return output;
};

const MarkdownReadOnly = ({ content, slug, projectId }) => {
  const lines = String(content || "").replace(/\r\n/g, "\n").split("\n");
  const blocks = [];
  for (let i = 0; i < lines.length;) {
    const line = lines[i];
    if (!line.trim()) { i += 1; continue; }
    const fence = line.trim().match(/^```\s*([^\s]*)/);
    if (fence) {
      const language = String(fence[1] || "").toLowerCase();
      const code = [];
      i += 1;
      while (i < lines.length && !/^\s*```/.test(lines[i])) code.push(lines[i++]);
      if (i < lines.length) i += 1;
      blocks.push(<Box key={`block-${i}`} sx={{ my: 1, maxWidth: "100%" }}>
        {language === "mermaid" && <Typography variant="caption" color="text.secondary">Mermaid source (graphical rendering not available)</Typography>}
        <Box component="pre" sx={{ m: 0, p: 1.5, bgcolor: "action.hover", borderRadius: 1, fontSize: 12, whiteSpace: "pre-wrap", overflowWrap: "anywhere", maxWidth: "100%" }}>{code.join("\n")}</Box>
      </Box>);
      continue;
    }
    const imageMatch = line.trim().match(/^!\[([^\]]*)\]\(([^)]+)\)$/);
    if (imageMatch) {
      blocks.push(<ManagedDocumentImage key={`image-${i++}`} slug={slug} projectId={projectId} reference={imageMatch[2]} alt={imageMatch[1]} />);
      continue;
    }
    const heading = line.trim().match(/^(#{1,6})\s+(.*)/);
    if (heading) {
      const level = heading[1].length;
      const variant = level === 1 ? "h6" : level === 2 ? "subtitle1" : "subtitle2";
      blocks.push(<Typography key={`block-${i++}`} variant={variant} sx={{ fontWeight: 700, mt: 1.5, mb: 0.75, overflowWrap: "anywhere" }}>{inlineText(heading[2], `heading-${i}`)}</Typography>);
      continue;
    }
    if (/^\s*(-|\*|\d+\.)\s+/.test(line)) {
      const ordered = /^\s*\d+\./.test(line);
      const entries = [];
      while (i < lines.length && /^\s*(-|\*|\d+\.)\s+/.test(lines[i])) entries.push(lines[i++].replace(/^\s*(-|\*|\d+\.)\s+/, ""));
      blocks.push(<Box key={`block-${i}`} component={ordered ? "ol" : "ul"} sx={{ pl: 3, my: 1 }}>{entries.map((entry, k) => <Box component="li" key={k} sx={{ fontSize: 14, overflowWrap: "anywhere" }}>{inlineText(entry, `list-${i}-${k}`)}</Box>)}</Box>);
      continue;
    }
    if (/^\s*(---+|\*\*\*+)\s*$/.test(line)) { blocks.push(<Divider key={`block-${i++}`} sx={{ my: 1 }} />); continue; }
    if (/^\s*>\s?/.test(line)) {
      const quote = [];
      while (i < lines.length && /^\s*>\s?/.test(lines[i])) quote.push(lines[i++].replace(/^\s*>\s?/, ""));
      blocks.push(<Box key={`block-${i}`} sx={{ borderLeft: "3px solid", borderColor: "divider", pl: 1.5, my: 1, overflowWrap: "anywhere" }}>{inlineText(quote.join(" "), `quote-${i}`)}</Box>);
      continue;
    }
    const paragraph = [];
    while (i < lines.length && lines[i].trim() && !/^!\[[^\]]*\]\([^)]+\)$/.test(lines[i].trim()) && !/^(\s*```|\s*#{1,6}\s|\s*(-|\*|\d+\.)\s+|\s*>\s?|\s*---+\s*$)/.test(lines[i])) paragraph.push(lines[i++].trim());
    if (!paragraph.length) { i += 1; continue; }
    blocks.push(<Typography key={`block-${i}`} variant="body2" sx={{ mb: 1.25, lineHeight: 1.7, overflowWrap: "anywhere" }}>{inlineText(paragraph.join(" "), `paragraph-${i}`)}</Typography>);
  }
  return blocks.length ? <>{blocks}</> : <Typography variant="body2" color="text.secondary">This document has no content yet.</Typography>;
};

const PlanningDetails = ({ document, summary, projectId, fullChangeId, onOpenDocument }) => (
  <Stack spacing={1} sx={{ minWidth: 0 }}>
    <Typography variant="subtitle2">Document Details</Typography>
    <Typography variant="body2"><strong>Category:</strong> Planning</Typography>
    <Typography variant="body2"><strong>Full Change:</strong> #{fullChangeId}</Typography>
    <Typography variant="body2"><strong>Version history:</strong> {document?.versions?.length ?? summary?.version_count ?? 0} saved versions</Typography>
    <Typography variant="body2"><strong>Updated:</strong> {readableTime(document?.updated_at || summary?.updated_at)}</Typography>
    {Boolean(document?.last_updated_by) && <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}><strong>By:</strong> {document.last_updated_by}</Typography>}
    <Typography variant="body2"><strong>Modules:</strong> {Array.isArray(document?.module_names) && document.module_names.length ? document.module_names.join(", ") : "None"}</Typography>
    {Boolean(document?.description) && <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>{document.description}</Typography>}
    <Divider />
    <Button size="small" variant="outlined" onClick={() => onOpenDocument?.({ slug: document.slug, projectId, showHistory: false })} disabled={!onOpenDocument} sx={{ textAlign: "left" }}>Open in Document Manager</Button>
    <Button size="small" variant="text" onClick={() => onOpenDocument?.({ slug: document.slug, projectId, showHistory: true })} disabled={!onOpenDocument}>Version History in Document Manager</Button>
  </Stack>
);

const FullChangePlanningDocumentsPanel = ({ projectId, fullChangeId, refreshVersion = 0, onOpenDocument }) => {
  const theme = useTheme();
  const narrowScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [documents, setDocuments] = useState([]);
  const [selectedSlug, setSelectedSlug] = useState("");
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [documentLoading, setDocumentLoading] = useState(false);
  const [error, setError] = useState("");
  const [documentError, setDocumentError] = useState("");
  const [expanded, setExpanded] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const listRequest = useRef(0);
  const docRequest = useRef(0);
  const scope = `${projectId || ""}::${fullChangeId || ""}`;

  const refresh = useCallback(async () => {
    const sequence = ++listRequest.current;
    ++docRequest.current;
    setDocuments([]);
    setSelectedDocument(null);
    setSelectedSlug("");
    setError("");
    setDocumentError("");
    setLoading(true);
    if (!projectId || !fullChangeId) { setLoading(false); return; }
    try {
      const params = new URLSearchParams({ project_id: String(projectId), category: "Planning", full_change_id: String(fullChangeId) });
      const response = await fetch(`/api/docs/documents?${params.toString()}`, { headers: docsHeaders() });
      const payload = await response.json();
      if (!response.ok || !payload.success) throw new Error(payload.error || "Could not load planning documents.");
      if (sequence !== listRequest.current) return;
      const rows = (Array.isArray(payload.documents) ? payload.documents : []).filter((row) => isMatchingPlanningDoc(row, projectId, fullChangeId));
      setDocuments(rows);
      setSelectedSlug(rows[0]?.slug || "");
    } catch (err) {
      if (sequence === listRequest.current) setError(err.message || "Could not load planning documents.");
    } finally {
      if (sequence === listRequest.current) setLoading(false);
    }
  }, [projectId, fullChangeId]);

  useEffect(() => {
    refresh();
    return () => { ++listRequest.current; ++docRequest.current; };
  }, [refresh, refreshVersion]);
  useEffect(() => {
    const sequence = ++docRequest.current;
    setSelectedDocument(null);
    setDocumentError("");
    if (!selectedSlug || !projectId || !fullChangeId) { setDocumentLoading(false); return undefined; }
    setDocumentLoading(true);
    (async () => {
      try {
        const params = new URLSearchParams({ project_id: String(projectId) });
        const response = await fetch(`/api/docs/documents/${encodeURIComponent(selectedSlug)}?${params.toString()}`, { headers: docsHeaders() });
        const payload = await response.json();
        if (!response.ok || !payload.success) throw new Error(payload.error || "Could not load the selected document.");
        if (!isMatchingPlanningDoc(payload.document, projectId, fullChangeId) || payload.document.slug !== selectedSlug) throw new Error("Document is no longer linked to this Full Change.");
        if (sequence === docRequest.current) setSelectedDocument(payload.document);
      } catch (err) {
        if (sequence === docRequest.current) setDocumentError(err.message || "Could not load document.");
      } finally {
        if (sequence === docRequest.current) setDocumentLoading(false);
      }
    })();
    return () => { ++docRequest.current; };
  }, [scope, selectedSlug, projectId, fullChangeId]);

  const visibleDocs = useMemo(() => documents.filter((doc) => `${doc.title || ""} ${doc.slug || ""}`.toLowerCase().includes(search.trim().toLowerCase())), [documents, search]);
  const selectedSummary = documents.find((item) => item.slug === selectedSlug);

  const workspace = (
    <Box data-testid="full-change-planning-docs-workspace" sx={{ containerType: "inline-size", width: "100%", minWidth: 0 }}>
      <Box sx={{ display: "grid", gridTemplateColumns: "minmax(150px, 1fr) minmax(0, 2fr) minmax(155px, .85fr)", gap: 1.25, minWidth: 0,
        "@container (max-width: 780px)": { gridTemplateColumns: "minmax(0,1fr)" } }}>
        <Paper variant="outlined" sx={{ p: 1.25, minWidth: 0, maxHeight: expanded ? "75vh" : 380, overflowY: "auto" }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Planning Documents ({documents.length})</Typography>
          <TextField fullWidth size="small" label="Search documents" value={search} onChange={(event) => setSearch(event.target.value)} sx={{ mb: 1 }} inputProps={{ "data-testid": "full-change-planning-docs-search" }} />
          {visibleDocs.map((doc) => (
            <Button key={doc.slug} fullWidth size="small" onClick={() => setSelectedSlug(doc.slug)} data-testid={`full-change-planning-doc-${doc.slug}`}
              variant={selectedSlug === doc.slug ? "contained" : "text"}
              sx={{ display: "block", textAlign: "left", textTransform: "none", mb: 0.5, whiteSpace: "normal", overflowWrap: "anywhere", px: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{doc.title || doc.slug}</Typography>
              <Typography variant="caption" sx={{ opacity: 0.8 }}>Planning · {doc.version_count ?? 0} versions</Typography>
            </Button>
          ))}
          {!visibleDocs.length && <Typography variant="caption" color="text.secondary">No matching documents.</Typography>}
        </Paper>
        <Paper variant="outlined" sx={{ p: 1.5, minWidth: 0, maxHeight: expanded ? "75vh" : 380, overflow: "auto" }} data-testid="full-change-planning-docs-preview">
          {selectedSummary && <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, overflowWrap: "anywhere" }}>{selectedSummary.title}</Typography>}
          {documentLoading && <CircularProgress size={22} />}
          {documentError && <Alert severity="warning">{documentError}</Alert>}
          {selectedDocument && <MarkdownReadOnly content={selectedDocument.content} slug={selectedDocument.slug} projectId={projectId} />}
        </Paper>
        <Paper variant="outlined" sx={{ p: 1.25, minWidth: 0, overflowWrap: "anywhere", "@container (max-width: 780px)": { display: showDetails ? "block" : "none" } }}>
          {selectedDocument && <PlanningDetails document={selectedDocument} summary={selectedSummary} projectId={projectId} fullChangeId={fullChangeId} onOpenDocument={onOpenDocument} />}
        </Paper>
      </Box>
    </Box>
  );
  return (
    <Box data-testid="full-change-planning-documents-panel" sx={{ minWidth: 0, width: "100%" }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" useFlexGap flexWrap="wrap" spacing={1} sx={{ mb: 1 }}>
        <Typography variant="body2" color="text.secondary">Read-only documents from the unified Document Manager.</Typography>
        <Stack direction="row" spacing={1}>
          <Button size="small" variant="outlined" onClick={refresh} disabled={loading}>Refresh</Button>
          <Button size="small" variant="outlined" onClick={() => setShowDetails((v) => !v)}>Details</Button>
          <Button size="small" variant="contained" onClick={() => setExpanded(true)} disabled={!documents.length}>Expand view</Button>
        </Stack>
      </Stack>
      {loading && <CircularProgress size={24} />}
      {error && <Alert severity="warning" data-testid="full-change-planning-docs-error">{error}</Alert>}
      {!loading && !error && !documents.length && (
        <Alert severity="info" data-testid="full-change-planning-docs-empty">
          No Planning documents are linked to this Full Change yet. Manage them in the existing Document Manager; use the existing Full Change Planning ZIP Import or Document Manager to add them. No document has been created by enabling this panel.
        </Alert>
      )}
      {!expanded && !loading && !error && Boolean(documents.length) && workspace}
      <Dialog open={expanded} onClose={() => setExpanded(false)} fullWidth maxWidth="xl" fullScreen={narrowScreen} aria-labelledby="full-change-planning-docs-expanded-title">
        <DialogTitle id="full-change-planning-docs-expanded-title">
          Planning Documentation — Full Change #{fullChangeId}
          <Button onClick={() => setExpanded(false)} sx={{ float: "right" }}>Close</Button>
        </DialogTitle>
        <DialogContent dividers sx={{ minWidth: 0 }}>{expanded && workspace}</DialogContent>
      </Dialog>
    </Box>
  );
};

export default FullChangePlanningDocumentsPanel;
